import { useEffect, useRef, useState } from "react";

import stylesFindFrame from "../../styles/components/FindFrame.module.css";

import { ReactComponent as DownIcon } from "../../assets/icons/down.svg";
import { ReactComponent as ResetIcon } from "../../assets/icons/reset.svg";

import Avatar from "../../ui/avatar/Avatar";

export const EMPTY_MAIL_FILTERS = {
  responded: null,
  status: null,
  rejected: null,
  responsibleId: null,
};

const RESPONDED_OPTIONS = [
  { value: null, label: "Todos" },
  { value: true, label: "Respondidos" },
  { value: false, label: "Sin respuesta" },
];

const STATUS_OPTIONS = [
  { value: null, label: "Todos" },
  { value: true, label: "Activos" },
  { value: false, label: "Inactivos" },
];

const REJECTED_OPTIONS = [
  { value: null, label: "Todos" },
  { value: true, label: "Rechazados" },
  { value: false, label: "No rechazados" },
];

function FilterRadioGroup({ label, value, options, onChange }) {
  return (
    <div
      className={stylesFindFrame.byVisibilityParent}
      role="radiogroup"
      aria-label={label}
    >
      <div className={stylesFindFrame.byVisibility}>{label}</div>

      <div className={stylesFindFrame.frameGroup}>
        {options.map((option) => (
          <div
            key={`${label}-${String(option.value)}`}
            className={stylesFindFrame.radioButtonParent}
            role="radio"
            tabIndex={0}
            aria-checked={value === option.value}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onChange(option.value);
              }
            }}
            style={{ cursor: "pointer" }}
          >
            <div className={stylesFindFrame.radioButton}>
              <div
                className={`${stylesFindFrame.radioButtonInner} ${
                  value === option.value ? stylesFindFrame.selected : ""
                }`}
              />
            </div>
            <div className={stylesFindFrame.visible}>{option.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const MailsFindFrame = ({
  appliedFilters,
  mailAccountOptions = [],
  onlyResponsible = false,
  onApply,
  onClose,
  anchorRef,
}) => {
  const [draftFilters, setDraftFilters] = useState({
    ...EMPTY_MAIL_FILTERS,
    ...appliedFilters,
  });
  const [isResponsibleDropdownOpen, setIsResponsibleDropdownOpen] =
    useState(false);

  const findFrameRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideFindFrame =
        findFrameRef.current && findFrameRef.current.contains(event.target);

      const clickedInsideAnchor =
        anchorRef?.current && anchorRef.current.contains(event.target);

      if (!clickedInsideFindFrame && !clickedInsideAnchor) {
        onClose?.();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [anchorRef, onClose]);

  const selectedResponsible =
    mailAccountOptions.find(
      (option) => option.id === draftFilters.responsibleId,
    ) ?? null;

  const setDraftValue = (filterKey, filterValue) => {
    setDraftFilters((prev) => ({ ...prev, [filterKey]: filterValue }));
  };

  const handleApply = () => {
    onApply?.({ ...draftFilters });
    onClose?.();
  };

  const handleReset = () => {
    setDraftFilters({ ...EMPTY_MAIL_FILTERS });
    setIsResponsibleDropdownOpen(false);
  };

  return (
    <div
      ref={findFrameRef}
      className={stylesFindFrame.frameParent}
      style={{ width: 380 }}
    >
      <div className={stylesFindFrame.filters}>
        {!onlyResponsible && (
          <>
            <FilterRadioGroup
              label="Respondido"
              value={draftFilters.responded}
              options={RESPONDED_OPTIONS}
              onChange={(value) => setDraftValue("responded", value)}
            />

            <FilterRadioGroup
              label="Estado"
              value={draftFilters.status}
              options={STATUS_OPTIONS}
              onChange={(value) => setDraftValue("status", value)}
            />

            <FilterRadioGroup
              label="Rechazado"
              value={draftFilters.rejected}
              options={REJECTED_OPTIONS}
              onChange={(value) => setDraftValue("rejected", value)}
            />
          </>
        )}

        <div className={stylesFindFrame.inputdropdownUser}>
          <div className={stylesFindFrame.byVisibility}>Casilla</div>

          <div
            className={stylesFindFrame.userParent}
            onClick={() => setIsResponsibleDropdownOpen((prev) => !prev)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setIsResponsibleDropdownOpen((prev) => !prev);
              }
            }}
            style={{ cursor: "pointer" }}
            role="button"
            tabIndex={0}
            aria-label="Filtrar por casilla"
          >
            <div className={stylesFindFrame.user}>
              <Avatar name={selectedResponsible?.name || "NA"} size={24} />

              <div className={stylesFindFrame.mailaccount}>
                {selectedResponsible
                  ? selectedResponsible.name
                  : "Todas las casillas"}
              </div>
            </div>

            <div className={stylesFindFrame.dropdownArrowWrapper}>
              <DownIcon className={stylesFindFrame.dropdownArrow} />
            </div>
          </div>

          {isResponsibleDropdownOpen && (
            <div className={stylesFindFrame.dropdownMenu}>
              <div
                className={stylesFindFrame.dropdownItem}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setDraftValue("responsibleId", null);
                  setIsResponsibleDropdownOpen(false);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setDraftValue("responsibleId", null);
                    setIsResponsibleDropdownOpen(false);
                  }
                }}
              >
                Todas las casillas
              </div>

              {mailAccountOptions.map((mailAccountOption) => (
                <div
                  key={mailAccountOption.id}
                  className={stylesFindFrame.dropdownItem}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setDraftValue("responsibleId", mailAccountOption.id);
                    setIsResponsibleDropdownOpen(false);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setDraftValue("responsibleId", mailAccountOption.id);
                      setIsResponsibleDropdownOpen(false);
                    }
                  }}
                >
                  <Avatar name={mailAccountOption.name} size={24} />

                  <span>{mailAccountOption.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={stylesFindFrame.buttonParent}>
          <div
            className={stylesFindFrame.button}
            onClick={handleApply}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleApply();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Aplicar filtros"
            style={{ cursor: "pointer" }}
          >
            <div className={stylesFindFrame.action}>Buscar</div>
          </div>

          <div
            className={stylesFindFrame.iconsresetWrapper}
            onClick={handleReset}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleReset();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Restablecer filtros"
            style={{ cursor: "pointer" }}
          >
            <div className={stylesFindFrame.iconsglass}>
              <ResetIcon className={stylesFindFrame.vectorIcon} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MailsFindFrame;
